import { formatDate } from "@/helpers/formatDate";
import { exportClientPurchases } from "@/services/clients/api";
import { Client } from "@/types/client";
import { QueryParamsBase } from "@/types/types";
import { notification } from "antd";
import ExcelJS from "exceljs";
import { useState } from "react";

type useExportClientPurchaseProps = {
    customer: Client;
    tableParams: QueryParamsBase;
};

export const useExportClientPurchase = (
    props: useExportClientPurchaseProps,
) => {
    const { customer, tableParams } = props;
    const [api, contextHolder] = notification.useNotification();
    const [loadingButtonExport, setLoadingButtonExport] = useState<boolean>(false);

    const openNotification = (
        type: "success" | "error" | "info" | "warning",
        placement: "topLeft" | "topRight" | "bottomLeft",
        message: string,
        description: string,
    ) => {
        api[type]({
            message: message,
            description: description,
            duration: 2.5,
            placement,
        });
    };

    const generateExcel = async () => {
        try {
            setLoadingButtonExport(true);

            openNotification(
                "info",
                "topLeft",
                "La descarga del archivo Excel está en proceso.",
                "Te agradecemos tu paciencia.",
            );

            const exportData = await exportClientPurchases(customer.id, tableParams);

            const workBook = new ExcelJS.Workbook();
            const sheet = workBook.addWorksheet("Compras");

            const fileName = "Compras - " + customer.name + ".xlsx";

            // ✅ Columnas EXACTAS del backend
            sheet.columns = [
                { header: "N° Ticket", key: "number_ticket", width: 25 },
                { header: "Fecha de compra", key: "created_at", width: 25 },
                { header: "Total", key: "total", width: 20 },
            ];

            sheet.getRow(1).font = { bold: true };

            // ✅ Insertar datos correctamente
            exportData.items.forEach((item: any) => {
                sheet.addRow({
                    number_ticket: item.number_ticket ? item.number_ticket : "Sin n°",
                    created_at: formatDate(item.created_at),
                    total: item.total ?? 0,
                });
            });

            workBook.xlsx.writeBuffer().then((buffer: any) => {
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = fileName;
                anchor.click();
                window.URL.revokeObjectURL(url);

                openNotification(
                    "success",
                    "topLeft",
                    "Documento descargado con éxito",
                    "Ya puedes visualizar el archivo",
                );
            });

            setLoadingButtonExport(false);
        } catch (error: any) {
            console.error(error);

            openNotification(
                "error",
                "topLeft",
                "Error al exportar",
                "No se pudo generar el archivo Excel. Intenta nuevamente.",
            );

            setLoadingButtonExport(false);
        }
    };

    return {
        contextHolder,
        generateExcel,
        loadingButtonExport,
    };
};